import PhoneIcon from "@mui/icons-material/Phone";
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Link,
	Paper,
	Stack,
	TextField,
	Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import { PersonPhonenumber } from "../../api";
import {
	createPersonPhonenumberByPersonId,
	deletePersonPhonenumber,
	Source as NumberSource,
} from "../../api/person/phonenumber";

type Props = {
	personId: number;
	personPhoneNumber: PersonPhonenumber[];
	emailsEditable: boolean;
};

const PersonPhoneNumbers: React.FC<Props> = ({
	personId,
	personPhoneNumber = [],
	emailsEditable,
}) => {
	const [phoneNumbers, setPhoneNumbers] =
		useState<PersonPhonenumber[]>(personPhoneNumber);
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [newPhonenumber, setNewPhonenumber] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);

	const handleOpenDialog = () => {
		setNewPhonenumber("");
		setError("");
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setNewPhonenumber("");
		setError("");
	};

	const handleSavePhonenumber = () => {
		if (!newPhonenumber.trim()) {
			setError("Phone number is required");
			return;
		}
		setLoading(true);
		createPersonPhonenumberByPersonId(personId, newPhonenumber)
			.then((res) => {
				setPhoneNumbers((prev) => [...prev, res]);
				setLoading(false);
				handleCloseDialog();
			})
			.catch((err) => {
				setError(err.message);
				setLoading(false);
			});
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSavePhonenumber();
		}
	};

	const handleDeletePhonenumber = (id: number) => {
		deletePersonPhonenumber(personId, id).then(() => {
			setPhoneNumbers((prev) => prev.filter((pn) => pn.id !== id));
		});
	};

	return (
		<Paper sx={{ p: 2 }} data-component="phone-numbers-paper">
			<Stack spacing={2} data-component="phone-numbers-stack">
				{/* Display phone numbers */}
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					flexWrap="wrap"
					useFlexGap
					data-component="phone-numbers-row"
				>
					<PhoneIcon
						sx={{ color: "text.secondary" }}
						data-component="phone-icon"
					/>
					{phoneNumbers.length === 0 ? (
						<span data-component="no-phones-text">No phone numbers</span>
					) : (
						phoneNumbers.map((pn) => (
							<Tooltip
								key={pn.id}
								title={
									pn.source === NumberSource.MANUAL
										? "Manual Entry"
										: "System Entry"
								}
								data-component="phone-tooltip"
							>
								<Chip
									label={
										<Link href={`tel:${pn.phonenumber}`}>
											{pn.phonenumber}
										</Link>
									}
									onDelete={() => handleDeletePhonenumber(pn.id)}
									color={!pn.e164_phonenumber ? "error" : "default"}
									sx={{ "&.MuiChip-root": { userSelect: "text" } }}
									data-component="phone-chip"
								/>
							</Tooltip>
						))
					)}
				</Stack>

				{/* Add Phone button */}
				{emailsEditable && (
					<Stack
						direction="row"
						justifyContent="flex-end"
						data-component="add-phone-button-stack"
					>
						<Button
							variant="outlined"
							size="small"
							startIcon={<PhoneIcon />}
							onClick={handleOpenDialog}
							data-component="add-phone-button"
						>
							Add Phone
						</Button>
					</Stack>
				)}
			</Stack>

			{/* Add Phone Dialog */}
			<Dialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				maxWidth="xs"
				fullWidth
				data-component="add-phone-dialog"
			>
				<DialogTitle data-component="dialog-title">
					Add Phone Number
				</DialogTitle>
				<DialogContent data-component="dialog-content">
					<TextField
						fullWidth
						size="small"
						placeholder="Enter phone number..."
						value={newPhonenumber}
						onChange={(e) => {
							setNewPhonenumber(e.target.value);
							setError("");
						}}
						onKeyDown={handleKeyDown}
						error={!!error}
						helperText={error}
						autoFocus
						sx={{ mt: 1 }}
						data-component="phone-input"
					/>
				</DialogContent>
				<DialogActions data-component="dialog-actions">
					<Button onClick={handleCloseDialog} data-component="cancel-button">
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSavePhonenumber}
						disabled={loading || !newPhonenumber.trim()}
						data-component="save-phone-button"
					>
						{loading ? <CircularProgress size={20} /> : "Add"}
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
};

export default PersonPhoneNumbers;
